import {
  Box,
  HStack,
  IconButton,
  Input,
  Stack,
  Text,
  createListCollection,
} from "@chakra-ui/react";
import * as React from "react";
import { LuPlus, LuTrash } from "react-icons/lu";

import {
  RuleNode,
  SuppressionAudienceField,
} from "../types/suppression-audience";
import { Button } from "./button";
import { Field } from "./field";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "./select";

const OPERATORS = [
  { value: "eq", label: "equals" },
  { value: "gt", label: "greater than" },
  { value: "lt", label: "less than" },
];

const GROUP_TYPES = [
  { value: "and", label: "AND" },
  { value: "or", label: "OR" },
];

type Operator = "eq" | "gt" | "lt";

interface ComparisonNode {
  kind: "comparison";
  field: string;
  operator: Operator;
  value: string;
}

export interface GroupNode {
  kind: "group";
  type: "and" | "or";
  children: EditorNode[];
}

type EditorNode = ComparisonNode | GroupNode;

const DEFAULT_OPERATOR: Operator = "eq";

function emptyComparison(): ComparisonNode {
  return {
    kind: "comparison",
    field: "",
    operator: DEFAULT_OPERATOR,
    value: "",
  };
}

function emptyGroup(): GroupNode {
  return { kind: "group", type: "and", children: [] };
}

function isComparisonValid(node: ComparisonNode): boolean {
  return node.field !== "" && node.value.trim() !== "";
}

function isGroupValid(node: GroupNode, isRoot: boolean): boolean {
  if (node.children.length === 0) return isRoot;
  return node.children.every((c) => isEditorNodeValid(c));
}

function isEditorNodeValid(node: EditorNode): boolean {
  if (node.kind === "comparison") {
    return isComparisonValid(node);
  }
  return isGroupValid(node, false);
}

function editorNodeToRule(
  node: EditorNode,
  fields: SuppressionAudienceField[],
): RuleNode {
  if (node.kind === "comparison") {
    const field = fields.find((f) => f.id === node.field);
    const fieldNode: RuleNode = {
      type: "field",
      data: field ? field.path : [node.field],
    };
    const atomNode: RuleNode = {
      type: "atom",
      data: isNaN(Number(node.value)) ? node.value : Number(node.value),
    };
    return {
      type: node.operator,
      data: { a: fieldNode, b: atomNode },
    };
  }
  const children = node.children.map((c) => editorNodeToRule(c, fields));
  if (children.length === 0) {
    return { type: node.type, data: [] };
  }
  if (children.length === 1) {
    return children[0];
  }
  return { type: node.type, data: children };
}

export function buildRules(
  root: GroupNode,
  fields: SuppressionAudienceField[],
): RuleNode {
  return editorNodeToRule(root, fields);
}

export function isRootValid(root: GroupNode): boolean {
  return isGroupValid(root, true);
}

export function defaultRoot(): GroupNode {
  return emptyGroup();
}

const operatorsCollection = createListCollection({
  items: OPERATORS.map((o) => ({ value: o.value, label: o.label })),
});

const groupTypesCollection = createListCollection({
  items: GROUP_TYPES.map((g) => ({ value: g.value, label: g.label })),
});

interface ComparisonRowProps {
  node: ComparisonNode;
  showLabels: boolean;
  canRemove: boolean;
  fields: SuppressionAudienceField[];
  portalRef: React.RefObject<HTMLElement>;
  onChange: (node: ComparisonNode) => void;
  onRemove: () => void;
}

function ComparisonRow({
  node,
  showLabels,
  canRemove,
  fields,
  portalRef,
  onChange,
  onRemove,
}: ComparisonRowProps) {
  const fieldsCollection = React.useMemo(
    () =>
      createListCollection({
        items: fields.map((f) => ({ value: f.id, label: f.label })),
      }),
    [fields],
  );
  return (
    <HStack gap="2" align="flex-end">
      <Field label={showLabels ? "Field" : undefined} flex="1">
        <SelectRoot
          size="sm"
          value={node.field ? [node.field] : []}
          onValueChange={({ value }) => onChange({ ...node, field: value[0] })}
          collection={fieldsCollection}
        >
          <SelectTrigger>
            <SelectValueText placeholder="Select field" />
          </SelectTrigger>
          <SelectContent portalRef={portalRef}>
            {fieldsCollection.items.map((item) => (
              <SelectItem key={item.value} item={item}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
      </Field>
      <Field label={showLabels ? "Operator" : undefined} flex="1">
        <SelectRoot
          size="sm"
          value={node.operator ? [node.operator] : []}
          onValueChange={({ value }) =>
            onChange({ ...node, operator: value[0] as Operator })
          }
          collection={operatorsCollection}
        >
          <SelectTrigger>
            <SelectValueText placeholder="Select operator" />
          </SelectTrigger>
          <SelectContent portalRef={portalRef}>
            {operatorsCollection.items.map((item) => (
              <SelectItem key={item.value} item={item}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
      </Field>
      <Field label={showLabels ? "Value" : undefined} flex="1">
        <Input
          size="sm"
          placeholder="Enter value"
          value={node.value}
          onChange={(e) => onChange({ ...node, value: e.target.value })}
        />
      </Field>
      {canRemove && (
        <IconButton
          aria-label="Remove rule"
          variant="ghost"
          size="xs"
          colorPalette="red"
          onClick={onRemove}
        >
          <LuTrash />
        </IconButton>
      )}
    </HStack>
  );
}

interface GroupEditorProps {
  node: GroupNode;
  depth: number;
  canRemove: boolean;
  fields: SuppressionAudienceField[];
  portalRef: React.RefObject<HTMLElement>;
  onChange: (node: GroupNode) => void;
  onRemove: () => void;
}

function GroupEditor({
  node,
  depth,
  canRemove,
  fields,
  portalRef,
  onChange,
  onRemove,
}: GroupEditorProps) {
  const updateChild = (index: number, child: EditorNode) => {
    const children = node.children.map((c, i) => (i === index ? child : c));
    onChange({ ...node, children });
  };

  const removeChild = (index: number) => {
    onChange({
      ...node,
      children: node.children.filter((_, i) => i !== index),
    });
  };

  const addComparison = () => {
    onChange({ ...node, children: [...node.children, emptyComparison()] });
  };

  const addGroup = () => {
    onChange({ ...node, children: [...node.children, emptyGroup()] });
  };

  return (
    <Box
      borderWidth={depth > 0 ? "1" : undefined}
      borderRadius={depth > 0 ? "md" : undefined}
      p={depth > 0 ? "3" : undefined}
    >
      <Stack gap="3">
        {(node.children.length > 0 || canRemove) && (
          <HStack justify="space-between">
            {node.children.length > 0 && (
              <HStack gap="2" align="center">
                <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                  Combine using
                </Text>
                <SelectRoot
                  size="sm"
                  value={[node.type]}
                  onValueChange={({ value }) =>
                    onChange({ ...node, type: value[0] as "and" | "or" })
                  }
                  collection={groupTypesCollection}
                  width="24"
                >
                  <SelectTrigger>
                    <SelectValueText />
                  </SelectTrigger>
                  <SelectContent portalRef={portalRef}>
                    {groupTypesCollection.items.map((item) => (
                      <SelectItem key={item.value} item={item}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              </HStack>
            )}
            {canRemove && (
              <IconButton
                aria-label="Remove group"
                variant="ghost"
                size="xs"
                colorPalette="red"
                onClick={onRemove}
              >
                <LuTrash />
              </IconButton>
            )}
          </HStack>
        )}
        {node.children.length === 0 && depth === 0 && (
          <Text fontSize="sm" color="fg.muted">
            No rules configured. All customers will be suppressed. Add a rule to
            filter by specific criteria.
          </Text>
        )}
        {node.children.map((child, index) =>
          child.kind === "comparison" ? (
            <ComparisonRow
              key={index}
              node={child}
              showLabels={index === 0 && depth === 0}
              canRemove={depth === 0 || node.children.length > 1}
              fields={fields}
              portalRef={portalRef}
              onChange={(updated) => updateChild(index, updated)}
              onRemove={() => removeChild(index)}
            />
          ) : (
            <GroupEditor
              key={index}
              node={child}
              depth={depth + 1}
              canRemove={depth === 0 || node.children.length > 1}
              fields={fields}
              portalRef={portalRef}
              onChange={(updated) => updateChild(index, updated)}
              onRemove={() => removeChild(index)}
            />
          ),
        )}
        <HStack gap="2">
          <Button variant="ghost" size="xs" onClick={addComparison}>
            <LuPlus /> Add Rule
          </Button>
          <Button variant="ghost" size="xs" onClick={addGroup}>
            <LuPlus /> Add Group
          </Button>
        </HStack>
      </Stack>
    </Box>
  );
}

export interface RuleEditorProps {
  value: GroupNode;
  fields: SuppressionAudienceField[];
  portalRef: React.RefObject<HTMLElement>;
  onChange: (value: GroupNode) => void;
}

export function RuleEditor({
  value,
  fields,
  portalRef,
  onChange,
}: RuleEditorProps) {
  return (
    <GroupEditor
      node={value}
      depth={0}
      canRemove={false}
      fields={fields}
      portalRef={portalRef}
      onChange={onChange}
      onRemove={() => {}}
    />
  );
}
